import { Button, Checkbox, message, Table, Tabs, Tag, Tooltip, Typography } from "antd";
import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import PayoutForm from "../Components/PayoutForm";
import PayoutHistory from "../Components/PayoutHistory";
import AxiosService from "../services/APIService";
import { LocalStorageService } from "../services/LocalStorage";
const Commission = () => {
  const apiService = new AxiosService();
  const [loading, setLoading] = useState(false);
  const [activeKey, setActiveKey] = useState("referral");
  const [paginationLimit, setPaginationLimit] = useState(10);
  const [pageSizeOptions] = useState(["10", "20", "50", "100"]);

  const [referralList, setReferralList] = useState([]);
  const [affiliateList, setAffiliateList] = useState([]);
  const [affiliateTotalCommission, setAffiliateTotalCommission] = useState({
    totalCommissionEarned: 0,
    dueAmount: 0,
    paidAmount: 0,
  });
  const [referralTotalCommission, setReferralTotalCommission] = useState({
    totalCommissionEarned: 0,
    dueAmount: 0,
    paidAmount: 0,
  });

  const [totalCountAffiliate, setTotalCountAffiliate] = useState(0);
  const [pageAffiliate, setpageAffiliate] = useState(1);
  const [totalCountReferral, setTotalCountReferral] = useState(0);
  const [pageReferral, setpageReferral] = useState(1);

  // ✅ only store keys, not rows
  const [selectedReferralKeys, setSelectedReferralKeys] = useState([]);
  const [selectedAffiliateKeys, setSelectedAffiliateKeys] = useState([]);
  const [isPayoutModalVisible, setIsPayoutModalVisible] = useState(false);
  const userData = LocalStorageService.getItem("user");

  // ✅ derive selected rows based on keys
  const getSelectedKeys = () => (activeKey === "referral" ? selectedReferralKeys : selectedAffiliateKeys);
  const getSelectedRows = () => {
    const keys = getSelectedKeys();
    const list = activeKey === "referral" ? referralList : affiliateList;
    return list.filter((row) => keys.includes(row.key));
  };

  const calculateTotalSelectedAmount = useCallback(() => {
    return getSelectedRows().reduce((total, row) => total + parseFloat(row.commissionAmount || 0), 0);
  }, [activeKey, referralList, affiliateList, selectedReferralKeys, selectedAffiliateKeys]);

  const handleTabChange = (key) => {
    setActiveKey(key);
  };

  const getButtonStyle = (isActive) => ({
    padding: "10px 20px",
    borderRadius: "8px",
    border: `1px solid transparent`,
    background: isActive ? "linear-gradient(90deg, #f178e2 0%, #9b59f6 50%, #0ed2f7 100%)" : "#fff",
    color: isActive ? "#fff" : "#000",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.3s ease",
  });

  const getRowSelection = (activeTab) => {
    const isReferral = activeTab === "referral";
    const currentRows = isReferral ? referralList : affiliateList;
    const selectedKeys = isReferral ? selectedReferralKeys : selectedAffiliateKeys;
    const setSelectedKeys = isReferral ? setSelectedReferralKeys : setSelectedAffiliateKeys;
    const isRowSelectable = (row) => {
      return row.commissionStatus !== "PAID" && !["REQUESTED", "PAID", "COMPLETED"].includes(row.payoutStatus);
    };
    const selectableRows = currentRows.filter(isRowSelectable);
    const allSelectableSelected = selectableRows.length > 0 && selectableRows.every((row) => selectedKeys.includes(row.key));
    return {
      columnTitle: (
        <div style={{ display: "flex", alignItems: "center", marginLeft: "12px" }}>
          <Checkbox
            indeterminate={selectedKeys.length > 0 && selectedKeys.length < selectableRows.length}
            checked={allSelectableSelected}
            onChange={(e) => {
              if (e.target.checked) {
                setSelectedKeys(selectableRows.map((row) => row.key));
              } else {
                setSelectedKeys([]);
              }
            }}
            disabled={selectableRows.length === 0}
          />
        </div>
      ),
      selectedRowKeys: selectedKeys,
      onChange: (keys) => {
        const validKeys = keys.filter((key) => {
          const row = currentRows.find((r) => r.key === key);
          return row && isRowSelectable(row);
        });
        setSelectedKeys(validKeys);
      },
      getCheckboxProps: (record) => {
        const isDisabled = record.commissionStatus === "PAID" || ["REQUESTED", "PAID", "COMPLETED"].includes(record.payoutStatus);
        const isCompleted = record.payoutStatus === "COMPLETED";
        return {
          disabled: isDisabled,
          checked: isCompleted || selectedKeys.includes(record.key),
        };
      },
    };
  };

  const referralColumns = [
    {
      title: "Full Name",
      dataIndex: "fullName",
      key: "fullName",
      width: 200,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: 200,
    },
    {
      title: "Phone Number",
      dataIndex: "phoneNumber",
      key: "phoneNumber",
      width: 200,
    },
    {
      title: "Commission Amount",
      dataIndex: "commissionAmount",
      key: "commissionAmount",
      width: 150,
      render: (amount) => `$${amount}`,
    },
    {
      title: "Commission Status",
      dataIndex: "commissionStatus",
      key: "commissionStatus",
      width: 200,
      render: (status) => {
        let color = status === "PAID" ? "green" : "volcano";
        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      title: "Payout Status",
      dataIndex: "payoutStatus",
      key: "payoutStatus",
      width: 200,
      render: (status) => {
        let color = "blue";
        if (status === "REQUESTED") color = "orange";
        else if (status === "PAID") color = "green";
        else if (status === "COMPLETED") color = "purple";
        return status ? <Tag color={color}>{status}</Tag> : "-";
      },
    },
  ];

  const affiliateColumns = [
    {
      title: "Full Name",
      dataIndex: "fullName",
      key: "fullName",
      width: 200,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: 200,
    },
    {
      title: "Phone Number",
      dataIndex: "phoneNumber",
      key: "phoneNumber",
      width: 200,
    },
    {
      title: "Commission Amount",
      dataIndex: "commissionAmount",
      key: "commissionAmount",
      width: 150,
      render: (amount) => `$${amount}`,
    },
    {
      title: "Commission Status",
      dataIndex: "commissionStatus",
      key: "commissionStatus",
      width: 200,
      render: (status) => {
        let color = status === "PAID" ? "green" : "volcano";
        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      title: "Payout Status",
      dataIndex: "payoutStatus",
      key: "payoutStatus",
      width: 200,
      render: (status) => {
        let color = "blue";
        if (status === "REQUESTED") color = "orange";
        else if (status === "PAID") color = "green";
        else if (status === "COMPLETED") color = "purple";
        return status ? <Tag color={color}>{status}</Tag> : "-";
      },
    },
  ];
  useEffect(() => {
    fetchRefferal(pageReferral);
  }, [pageReferral, paginationLimit]);

  useEffect(() => {
    fetchAffiliate(pageAffiliate);
  }, [pageAffiliate, paginationLimit]);

  useEffect(() => {
    fetchTotalCommission();
  }, []);

  const fetchRefferal = (currentPage = 1) => {
    setLoading(true);
    apiService
      .get(`/commission/user-commission-earning/${userData?._id}/REFERRAL?page=${currentPage}&limit=${paginationLimit}`)
      .then((res) => {
        if (res.success) {
          const formatted = res.payload.data.map((r) => ({
            key: r._id,
            commissionAmount: r.commissionAmount,
            commissionStatus: r.commissionStatus,
            createdAt: dayjs(r.createdAt).format("DD-MM-YYYY"),
            fullName: r.fullName,
            email: r.email,
            phoneNumber: r.phoneNumber,
            payoutStatus: r.payoutStatus,
          }));
          setReferralList(formatted);
          setTotalCountReferral(res.payload.count || 0);
        } else {
          message.error(res.message || "Failed to fetch referrals");
        }
      })
      .catch((e) => message.error(e.message || "Something went wrong"))
      .finally(() => setLoading(false));
  };

  const fetchAffiliate = (currentPage = 1) => {
    setLoading(true);
    apiService
      .get(`/commission/user-commission-earning/${userData._id}/AFFILIATE?page=${currentPage}&limit=${paginationLimit}`)
      .then((res) => {
        if (res.success) {
          const formatted = res.payload.data.map((a) => ({
            key: a._id,
            commissionAmount: a.commissionAmount,
            commissionStatus: a.commissionStatus,
            createdAt: dayjs(a.createdAt).format("DD-MM-YYYY"),
            fullName: a.fullName,
            email: a.email,
            phoneNumber: a.phoneNumber,
            payoutStatus: a.payoutStatus,
          }));
          setAffiliateList(formatted);
          setTotalCountAffiliate(res.payload.count || 0);
        } else {
          message.error(res.message || "Failed to fetch affiliates");
        }
      })
      .catch((e) => message.error(e.message || "Something went wrong"))
      .finally(() => setLoading(false));
  };

  const fetchTotalCommission = () => {
    apiService
      .get(`/commission/total-commission-earning/${userData._id}/AFFILIATE`)
      .then((res) => {
        if (res.success) {
          setAffiliateTotalCommission({
            totalCommissionEarned: res.payload.totalCommissionEarned || 0,
            dueAmount: res.payload.dueAmount || 0,
            paidAmount: res.payload.paidAmount || 0,
          });
        }
      })
      .catch((e) => message.error(e.message || "Something went wrong"));
    apiService
      .get(`/commission/total-commission-earning/${userData._id}/REFERRAL`)
      .then((res) => {
        if (res.success) {
          setReferralTotalCommission({
            totalCommissionEarned: res.payload.totalCommissionEarned || 0,
            dueAmount: res.payload.dueAmount || 0,
            paidAmount: res.payload.paidAmount || 0,
          });
        }
      })
      .catch((e) => message.error(e.message || "Something went wrong"));
  };

  const handlePayoutSubmit = async (values, payoutMethod) => {
    console.log("🚀 ~ handlePayoutSubmit ~ values:", values);
    try {
      const formValues = { ...values };
      let qrCodeFile = null;
      if (payoutMethod === "usdt" && values.qrCode) {
        qrCodeFile = Array.isArray(values.qrCode) ? values.qrCode[0]?.originFileObj : values.qrCode;
        delete formValues.qrCode;
      }
      const payoutData = {
        payoutMethod: payoutMethod === "usdt" ? "CRYPTO" : "BANK",
        payoutAmount: calculateTotalSelectedAmount(),
        commissionType: activeKey === "referral" ? "REFERRAL" : "AFFILIATE",
        commissionID: getSelectedRows()?.map((row) => row.key) || [],
        ...(payoutMethod === "usdt" ? { cryptoDetails: { ...formValues } } : { bankDetails: { ...formValues } }),
      };
      const formData = new FormData();
      formData.append("payoutMethod", payoutData.payoutMethod);
      formData.append("payoutAmount", payoutData.payoutAmount);
      formData.append("commissionType", payoutData.commissionType);
      payoutData.commissionID.forEach((id) => {
        formData.append("commissionID[]", id);
      });
      if (payoutData.cryptoDetails) {
        formData.append("cryptoDetails", JSON.stringify(payoutData.cryptoDetails));
      }
      if (payoutData.bankDetails) {
        formData.append("bankDetails", JSON.stringify(payoutData.bankDetails));
      }
      
      // Append the QR code file if it exists
      if (qrCodeFile) {
        formData.append("qrCode", qrCodeFile);
      }
      const response = await apiService.post("/payout/user/create-payout-request", formData);
      if (response.success) {
        message.success(response.message || "Payout request submitted successfully!");
        if (activeKey === "referral") {
          setSelectedReferralKeys([]);
          fetchRefferal(pageReferral);
        } else {
          setSelectedAffiliateKeys([]);
          fetchAffiliate(pageAffiliate);
        }
        fetchTotalCommission();
        handleClosePayoutModal();
      }
    } catch (error) {
      message.error(error?.response?.data?.message || error.message || "Failed to process payout. Please try again.");
    }
  };

  const handleClosePayoutModal = () => {
    setIsPayoutModalVisible(false);
  };

  const handlePayoutRequest = () => {
    setIsPayoutModalVisible(true);
  };

  return (
    <div>
      {/* Header with payout button */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <Typography.Title level={3} className="page-title" style={{ margin: 0 }}>
          Commission
        </Typography.Title>
        <Tooltip
          title={
            getSelectedKeys().length > 0
              ? `Minimum payout amount is $100 (Current: $${calculateTotalSelectedAmount().toFixed(2)})`
              : "Select commissions to request payout"
          }
          placement="left"
        >
          <div>
            <Button
              type="primary"
              onClick={handlePayoutRequest}
              disabled={getSelectedKeys().length === 0 || calculateTotalSelectedAmount() < 100}
              style={{
                padding: "10px 24px",
                height: "auto",
                fontWeight: 500,
                background: getSelectedKeys().length > 0 && calculateTotalSelectedAmount() >= 100 ? "linear-gradient(90deg, #f178e2 0%, #9b59f6 50%, #0ed2f7 100%)" : "#f0f0f0",
                border: "none",
                color: getSelectedKeys().length > 0 && calculateTotalSelectedAmount() >= 100 ? "#fff" : "rgba(0, 0, 0, 0.25)",
                cursor: getSelectedKeys().length > 0 && calculateTotalSelectedAmount() >= 100 ? "pointer" : "not-allowed",
                boxShadow: getSelectedKeys().length > 0 && calculateTotalSelectedAmount() >= 100 ? "0 2px 8px rgba(155, 89, 246, 0.3)" : "none",
              }}
            >
              Request Payout ({getSelectedKeys().length} Selected)
            </Button>
          </div>
        </Tooltip>
      </div>

      {/* Tabs for referral/affiliate */}
      <div className="tabs-container" style={{ marginBottom: 24 }}>
        <div className="tabs-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <div className="tabs" style={{ display: "flex", gap: 12 }}>
            {[
              { key: "referral", label: "Referral Commission" },
              { key: "affiliate", label: "Affiliate Commission" },
              { key: "payoutHistory", label: "Payout History" },
            ].map((tab) => (
              <div
                key={tab.key}
                onClick={() => handleTabChange(tab.key)}
                style={{
                  padding: "8px 16px",
                  borderRadius: "8px",
                  background: activeKey === tab.key ? "linear-gradient(90deg, #f178e2 0%, #9b59f6 50%, #0ed2f7 100%)" : "#fff",
                  border: activeKey === tab.key ? "none" : "1px solid #e8e8e8",
                  cursor: "pointer",
                  fontWeight: 500,
                  color: activeKey === tab.key ? "#fff" : "rgba(0, 0, 0, 0.85)",
                  transition: "all 0.3s ease",
                  boxShadow: activeKey === tab.key ? "0 2px 8px rgba(155, 89, 246, 0.2)" : "none",
                }}
              >
                {tab.label}
              </div>
            ))}
          </div>
          {/* commission summary buttons */}
          {activeKey !== "payoutHistory" && (
            <div style={{ display: "flex", gap: "8px" }}>
              <Button type="primary" className="gradient-border-btn">
                {activeKey === "referral"
                  ? `Due Commission: $${referralTotalCommission.dueAmount}`
                  : `Due Commission: $${affiliateTotalCommission.dueAmount}`}
              </Button>
              <Button type="primary" className="gradient-border-btn">
                {activeKey === "referral"
                  ? `Paid Commission: $${referralTotalCommission.paidAmount}`
                  : `Paid Commission: $${affiliateTotalCommission.paidAmount}`}
              </Button>
              <Button type="primary" className="gradient-border-btn">
                {activeKey === "referral"
                  ? `Total Commission: $${referralTotalCommission.totalCommissionEarned}`
                  : `Total Commission: $${affiliateTotalCommission.totalCommissionEarned}`}
              </Button>
            </div>
          )}
        </div>
        {activeKey === "referral" && (
          <div className="table-container">
            <Table
              columns={referralColumns}
              dataSource={referralList}
              loading={loading}
              rowSelection={getRowSelection("referral")}
              pagination={{
                current: pageReferral,
                pageSize: paginationLimit,
                total: totalCountReferral,
                showSizeChanger: true,
                pageSizeOptions,
                onChange: (page, pageSize) => {
                  setpageReferral(page);
                  setPaginationLimit(pageSize);
                },
              }}
              scroll={{ x: true }}
            />
          </div>
        )}
        {activeKey === "affiliate" && (
          <div className="table-container">
            <Table
              columns={affiliateColumns}
              dataSource={affiliateList}
              loading={loading}
              rowSelection={getRowSelection("affiliate")}
              pagination={{
                current: pageAffiliate,
                pageSize: paginationLimit,
                total: totalCountAffiliate,
                showSizeChanger: true,
                pageSizeOptions,
                onChange: (page, pageSize) => {
                  setpageAffiliate(page);
                  setPaginationLimit(pageSize);
                },
              }}
              scroll={{ x: true }}
            />
          </div>
        )}
        {activeKey === "payoutHistory" && <PayoutHistory />}
      </div>
      <PayoutForm isVisible={isPayoutModalVisible} onClose={handleClosePayoutModal} onPayoutSubmit={handlePayoutSubmit} />
    </div>
  );
};
export default Commission;
